import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'

import { GET_BUSINESS_CLIENTS } from 'features/BusinessClients/queries-legacy'
import BusinessClientListView from 'features/BusinessClients/components/BusinessClientList/List'
import { useLogin } from 'features/Login/hooks'
import { BusinessClientFilters } from 'features/BusinessClients/components/BusinessClientList/Filters'
import { IBusinessClientFiltersForm } from 'features/BusinessClients/types'
import {
  mapFiltersToQueryVariables,
  mapFiltersToSearchQueryString,
  mapSearchToFiltersForm
} from 'features/BusinessClients/utils'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { PaginationList } from 'components/PaginationList'
import { withLastUrl } from 'features/App/components/WithLastUrl'

const BUSINESS_CLIENTS_PAGE_LIMIT = 30
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px 32px;
`

const messages = defineMessages({
  helmet: {
    id: 'companyClients.helmet',
    defaultMessage: 'Business Clients | Nexbus Admin'
  }
})

const BusinessClientsView = (): ReactElement => {
  let content
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const queries = getQueries(location.search)
  const { page = '1' } = queries
  const pageNb = parseInt(page)
  const login = useLogin()

  const businessClientAutocomplete = useBusinessClientAutocomplete(queries.businessClientId, {
    skip: !queries.businessClientId
  })
  const filters = mapSearchToFiltersForm(queries, {
    businessClient: businessClientAutocomplete
  })
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_BUSINESS_CLIENTS, {
    fetchPolicy: 'network-only',
    variables: {
      input: mapFiltersToQueryVariables(filters),
      pagination: {
        page: pageNb,
        size: BUSINESS_CLIENTS_PAGE_LIMIT
      }
    }
  })

  const handleFiltersSubmit = (values: IBusinessClientFiltersForm): void => {
    navigate(`/business-clients?${mapFiltersToSearchQueryString(values)}`)
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.BUSINESS_CLIENT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.COMPANY} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="companyClients.error.description"
            defaultMessage="There was an error while loading your clients"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="action.reload" defaultMessage="Reload" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.businessClients.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="companyClients.empty.filters.description"
            defaultMessage="There was no clients found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/business-clients" kind="primary">
            <FormattedMessage id="empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <BusinessClientListView clients={data.businessClients.items} />
        <PaginationList
          page={pageNb}
          limit={BUSINESS_CLIENTS_PAGE_LIMIT}
          totalItems={data.businessClients.total}
          currentItemsCount={data.businessClients.items.length}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="companyClients.header.label" defaultMessage="Business Clients List" />
        </Heading.H3>
        <HeaderActions>
          {canWrite(FEATURES.BUSINESS_CLIENT, login.accessToken.policies) && (
            <Button as={NavLink} to="/business-clients/new" kind="primary">
              <FormattedMessage id="companyClients.new.btn" defaultMessage="+ Add Client" />
            </Button>
          )}
        </HeaderActions>
      </ContentHeader>
      <ContentBody>
        <BusinessClientFilters filters={filters} onSubmit={handleFiltersSubmit} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(BusinessClientsView)
